import axios from "axios";
import { useEffect, useState } from "react";
import dayjs from "@shared/dayjs";

import { getPostJsonHeaders } from "@shared/axiosHelpers";
import formatPhoneNumber from "@shared/FormatPhoneNumber";
import { toTitleCase } from "@shared/Utilities";
import { formatNumber, formatPrice } from "../../SearchListings/components/helpers";
import { formatCallDuration } from "../../PhoneSystem/TrackingNumbers/helpers";
import { makeDateRangeFromPhrase } from "../../Reports/components/Leaderboards/helpers";
import { prospectDisabled } from "./helpers";

export const usePropertySearch = (
  locationQuery,
  filters,
  searchType,
  limit,
  mlsIds,
  setIsFirstLoad,
  includeDummyData = false,
) => {
  const [properties, setProperties] = useState([]);
  const [radius, setRadius] = useState(0);
  const [areaGeometry, setAreaGeometry] = useState(null);
  const [totalInArea, setTotalInArea] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  const updateProspectLeadStatus = (phoneNumber) => {
    const updated = properties.map((p) => {
      p.prospects = p.prospects.map((prospect) => {
        if (prospect.phone === phoneNumber) {
          return {
            ...prospect,
            isLead: true,
            isMyLead: true,
          };
        }

        return prospect;
      });

      return p;
    });

    setProperties(updated);
  };

  useEffect(() => {
    setError(null);
    setProperties([]);
    setAreaGeometry(null);

    if (!locationQuery || (searchType === "expireds" && !mlsIds.length)) {
      return;
    }

    let isCancelled = false;
    setIsFirstLoad(false);
    setIsSearching(true);

    const abortController = new AbortController();

    getProperties(locationQuery, filters, searchType, limit, mlsIds, includeDummyData, abortController)
      .then((result) => {
        if (!isCancelled) {
          setProperties(result.properties);
          setIsSearching(false);
          setRadius(result.radius);
          setAreaGeometry(result.areaGeometry);
          setTotalInArea(result.totalInArea);
        }
      })
      .catch((e) => {
        if (!isCancelled) {
          setIsSearching(false);
          setError(e);
        }
      });

    return () => {
      isCancelled = true;
      setIsSearching(false);
      abortController?.abort();
    };
  }, [locationQuery, filters, searchType, limit, mlsIds]);

  return { properties, radius, areaGeometry, totalInArea, isSearching, error, updateProspectLeadStatus };
};

export const addLead = async (prospect, property, agentId) => {
  const lead = {
    first_name: prospect.firstName,
    last_name: prospect.lastName,
    primary_agent_id: agentId,
    intent: "seller",
    status: "new",
    source: "Expired",
    stage: "New lead",
    type_name: "lead",
    street_address: property.streetAddress,
    city: property.city,
    locality: property.state,
    postal_code: property.postalCode,
    phone: prospect.phone,
    is_lead: true,
  };

  // TODO: Consider adding `slug` to the POST /api/v3/leads response. Any reason it wouldn't be there already / external concerns?
  const createResponse = await axios.post("/api/v3/leads", lead, getPostJsonHeaders());
  const { data } = await axios.get(
    `/api/v4/person_detail/show/${createResponse.data.lead.person_id}`,
    getPostJsonHeaders(),
  );

  return data.data.attributes.slug;
};

export const getLandvoiceMlsIds = async () => {
  const response = await axios.get("/opportunities/mls_ids");

  return response.data?.mls_ids || [];
};

export const getSavedSearches = async (userId, searchType) => {
  const response = await axios.get(
    `/users/${userId}/opportunities_saved_searches?search_type=${searchType.toLowerCase()}`,
  );

  return response.data?.saved_searches;
};

export const saveSearch = async (userId, name, search, searchType) => {
  const data = { saved_search: { name, search_type: searchType.toLowerCase(), search } };

  await axios.post(`/users/${userId}/opportunities_saved_searches`, data, getPostJsonHeaders());
};

export const deleteSearch = async (userId, searchId) => {
  await axios.delete(`/users/${userId}/opportunities_saved_searches/${searchId}`, getPostJsonHeaders());
};

export const saveMostRecentSearch = (searchType, locationQuery, filters, limit) => {
  const search = JSON.stringify({
    searchType,
    locationQuery,
    filters,
    limit,
  });

  window.localStorage.setItem(RecentSearchLocalKey, search);
};

export const loadMostRecentSearch = () => {
  return JSON.parse(window.localStorage.getItem(RecentSearchLocalKey));
};

const RecentSearchLocalKey = "opportunities-recent-search";

const getProperties = async (
  location,
  filters,
  searchType,
  limit,
  mlsIds,
  includeDummyData,
  abortController,
) => {
  if (!location) {
    return [];
  }

  const query = Object.entries(filters).reduce((acc, [key, val]) => {
    if (val) {
      acc[key] = val;
    }

    return acc;
  }, {});

  if (location.isRadius) {
    query.lat = location.location.lat;
    query.lng = location.location.lng;
  } else if (location.isAreaSearch) {
    query.areaId = location.areaId;
  } else if (location.isAddressSearch) {
    query.addressId = location.addressId;
  } else {
    query.polygon = location.polygon.map(({ lat, lng }) => ({ latitude: lat, longitude: lng }));
  }

  if (filters.communicationRange) {
    const { from, to } = makeDateRangeFromPhrase(new Date().getFullYear(), filters.communicationRange);
    query.commsFrom = from;
    query.commsTo = to;
  }

  query.statusDateMin = filters.statusDateMin ? new Date(filters.statusDateMin) : null;
  query.statusDateMax = filters.statusDateMax ? new Date(filters.statusDateMax) : null;
  query.limit = limit;
  query.mlsIds = mlsIds;

  const options = {
    ...getPostJsonHeaders(),
    signal: abortController?.signal,
  };

  const results = await axios.post(`/opportunities/${searchType}`, query, options);

  return {
    properties:
      results.data?.properties?.map((p) => formatProperty(p, includeDummyData)).filter((p) => p) || [],
    radius: results.data?.radius || 0,
    areaGeometry: results.data?.geometry,
    totalInArea: results.data?.total_in_area,
  };
};

const formatProperty = (property, includeDummyData) => {
  if (!property.city || !property.streetAddress || !property.state) {
    return null;
  }

  const fullAddress = `${property.streetAddress}, ${property.city}, ${property.state} ${property.postalCode}`;
  const streetViewLink = property.streetAddress ? `https://www.google.com/maps/place/${fullAddress}` : "";
  const zillowLink = property.streetAddress
    ? `https://www.zillow.com/homes/${fullAddress.replace(" ", "-")}`
    : "";

  const prospects =
    property.prospects?.map((p, i) => ({
      ...p,
      name: toTitleCase(p.name), // These come in as all caps
      firstName: toTitleCase(p.firstName),
      lastName: toTitleCase(p.lastName),
      phoneFormatted: formatProspectPhoneNumber(p.phone, p),
      isRecommended: i === 0,
    })) || [];

  const hasAnyPhoneNumbers = prospects.some((p) => p.phoneFormatted);

  if (includeDummyData && prospects.length) {
    prospects[0] = FakeProspect;
  }

  const callHistory =
    property.callHistory?.map((call) => ({
      ...call,
      phoneDisplay: formatPhoneNumber(call.phone),
      timestampDisplay: dayjs(call.timestamp).format("L"),
      durationDisplay: formatCallDuration(call.duration),
      name: toTitleCase(call.name),
    })) || [];

  return {
    ...property,
    mortgageDateDisplay: property.mortgageDate ? property.mortgageDate.replace(/^(.+\d{4}).+$/, "$1") : "",
    auctionDateDisplay: property.auctionDate ? property.auctionDate.replace(/^(.+\d{4}).+$/, "$1") : "",
    statusDateDisplay: property.statusDate ? dayjs(property.statusDate.split("T")[0]).format("L") : "",
    recommendedProspect: prospects?.[0] || {},
    listPriceFormatted: formatPrice(property.listPrice),
    sqftFormatted: formatNumber(property.sqft),
    loanAmount: formatPrice(property.loanAmount),
    delinquentAmount: formatPrice(property.delinquentAmount),
    attorneyPhone: formatPhoneNumber(property.attorneyPhone),
    fullAddress,
    streetViewLink,
    zillowLink,
    prospects,
    callHistory,
    hasReceivedCalls: callHistory.length > 0,
    hasAnyPhoneNumbers,
  };
};

const formatProspectPhoneNumber = (phone, prospect) => {
  let formatted = formatPhoneNumber(phone);

  if (!formatted) {
    return formatted;
  }

  if (prospectDisabled(prospect)) {
    formatted = formatted.slice(0, -4) + "****";
  }

  return formatted;
};

const FakeProspect = {
  name: "Matt Garay",
  firstName: "Matt",
  lastName: "Garay",
  phone: "+18056260399",
  isDnc: false,
  isLead: false,
  isMyPondLead: false,
  isMyLead: false,
  phoneFormatted: "+1 (805) 626-0399",
  isRecommended: true,
};
